import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatRequest, ChatResponse, ConfirmRequest, LanguageCode, MeResponse, PatientBanner, Slot, UserPreferences } from '@ampshift/shared';
import { api, ApiError } from './api';
import { authStore, onSignOut, signOut } from './auth';
import { useStore } from './store';
import { applyDocumentLanguage, applyTheme, localSettingsStore, updateLocalSettings } from './settings/local';
import { useT, useUiLanguage } from './i18n/useT';
import { LoginScreen } from './components/LoginScreen';
import { TopBar } from './components/TopBar';
import { Thread } from './components/Thread';
import { Composer, COMPOSER_ATTR } from './components/Composer';
import { SettingsPanel } from './components/SettingsPanel';
import type { PatientContextState } from './components/PatientIndicator';
import { SurfaceActionsContext, type SurfaceActions } from './surfaces/context';
import { nextTurnId, type Turn } from './conversation';
import { useVoiceCapture } from './voice/useVoiceCapture';
import { primeAudioContext } from './voice/audio-worklet';
import type { VoiceCommand, VoiceConfig } from './voice/controller';
import { createSpeaker, detectSpeechLanguage } from './voice/speaker';
import { DEFAULT_PUSH_TO_TALK_SHORTCUT, DEFAULT_TOGGLE_SHORTCUT, isShortcutAllowedTarget, matchesShortcut, releasesShortcut } from './voice/shortcuts';

export default function App(): JSX.Element {
  const auth = useStore(authStore);
  const lang = useUiLanguage();
  const t = useT();
  useEffect(() => applyDocumentLanguage(lang), [lang]);
  const theme = useStore(localSettingsStore).theme;
  useEffect(() => applyTheme(theme), [theme]);
  if (auth.status === 'signedOut') return <LoginScreen {...(auth.reason === 'expired' ? { notice: t('sessionExpired') } : {})} />;
  return <Workspace key={auth.status === 'dev' ? auth.principal.userId : auth.username} />;
}

function Workspace(): JSX.Element {
  const t = useT();
  const local = useStore(localSettingsStore);
  const [me, setMe] = useState<MeResponse | undefined>();
  const [meError, setMeError] = useState<string | undefined>();
  const [preferences, setPreferences] = useState<UserPreferences>({ voiceOutputEnabled: false, listeningMode: 'PRESS_TO_TOGGLE' });
  const [context, setContext] = useState<PatientContextState>({ activePatient: null, activeEncounterId: null });
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const conversationId = useRef<string | undefined>(undefined);

  // ---------------------------------------------------------------- bootstrap
  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((m) => {
        if (cancelled) return;
        setMe(m);
        setPreferences(m.preferences);
        setContext(m.context);
        if (!localSettingsStore.get().inputLanguage || !m.voiceDefaults.allowedLanguages.includes(localSettingsStore.get().inputLanguage)) {
          const preferred = (m.preferences.preferredLanguage ?? m.voiceDefaults.defaultLanguage) as LanguageCode;
          if (localSettingsStore.get().inputLanguage !== 'auto') updateLocalSettings({ inputLanguage: preferred });
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setMeError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------------------------------------------- effective voice settings
  const effective = useMemo(() => {
    const d = me?.voiceDefaults;
    return {
      shortcut: preferences.shortcut ?? d?.shortcut ?? DEFAULT_TOGGLE_SHORTCUT,
      pushToTalkShortcut: local.pushToTalkShortcut ?? d?.pushToTalkShortcut ?? DEFAULT_PUSH_TO_TALK_SHORTCUT,
      silenceThresholdMs: preferences.silenceThresholdMs ?? d?.silenceThresholdMs ?? 1200,
      maxCommandSeconds: d?.maxCommandSeconds ?? 30,
      mode: preferences.listeningMode,
      allowAuto: (d?.allowedLanguages.length ?? 0) > 1,
    };
  }, [me, preferences, local.pushToTalkShortcut]);
  const effectiveRef = useRef(effective);
  effectiveRef.current = effective;
  const localRef = useRef(local);
  localRef.current = local;
  const prefsRef = useRef(preferences);
  prefsRef.current = preferences;

  // ---------------------------------------------------------------- speaker
  const speaker = useMemo(() => createSpeaker({ api, enabled: () => prefsRef.current.voiceOutputEnabled }), []);
  const speakerState = useStore(speaker.store);
  useEffect(() => () => speaker.dispose(), [speaker]);

  // ---------------------------------------------------------------- conversation
  const applyResponse = useCallback(
    (res: ChatResponse) => {
      conversationId.current = res.conversationId;
      setContext(res.context);
      setTurns((prev) => [...prev, { id: nextTurnId(), role: 'assistant', text: res.text, surfaces: res.surfaces, at: Date.now() }]);
      if (res.speakableText && prefsRef.current.voiceOutputEnabled) {
        const l = localRef.current;
        const dialect: LanguageCode = l.inputLanguage !== 'auto' && l.inputLanguage.startsWith('ar') ? l.inputLanguage : 'ar-AE';
        const language = detectSpeechLanguage(res.speakableText, dialect);
        const voiceId = language.startsWith('ar') ? l.arabicVoiceId : l.englishVoiceId;
        void speaker.speak(res.speakableText, language, voiceId);
      }
    },
    [speaker],
  );

  const pushError = useCallback((err: unknown) => {
    const message = err instanceof ApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : String(err);
    setTurns((prev) => [...prev, { id: nextTurnId(), role: 'assistant', text: '', surfaces: [{ type: 'error', code: err instanceof ApiError ? err.code : 'CLIENT_ERROR', message, recoverable: true }], status: 'error', at: Date.now() }]);
  }, []);

  const sendMessage = useCallback(
    async (text: string, opts: { inputMode?: 'text' | 'voice'; transcript?: ChatRequest['transcript']; form?: ChatRequest['form']; display?: string } = {}) => {
      speaker.stop();
      const inputMode = opts.inputMode ?? 'text';
      const userTurn: Turn = { id: nextTurnId(), role: 'user', text: opts.display ?? text, surfaces: [], inputMode, at: Date.now() };
      setTurns((prev) => [...prev, userTurn]);
      setBusy(true);
      try {
        const l = localRef.current;
        const req: ChatRequest = {
          message: text,
          inputMode,
          ...(conversationId.current ? { conversationId: conversationId.current } : {}),
          ...(opts.transcript ? { transcript: opts.transcript } : {}),
          ...(opts.form ? { form: opts.form } : {}),
          locale: l.uiLanguage === 'ar' ? (l.inputLanguage !== 'auto' && l.inputLanguage.startsWith('ar') ? l.inputLanguage : 'ar-AE') : 'en-US',
          clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        applyResponse(await api.chat(req));
      } catch (err) {
        pushError(err);
      } finally {
        setBusy(false);
      }
    },
    [applyResponse, pushError, speaker],
  );

  const confirm = useCallback(
    async (req: ConfirmRequest) => {
      speaker.stop();
      setBusy(true);
      try {
        applyResponse(await api.confirm({ ...req, ...(conversationId.current ? { conversationId: conversationId.current } : {}) }));
      } catch (err) {
        pushError(err);
      } finally {
        setBusy(false);
      }
    },
    [applyResponse, pushError, speaker],
  );

  const surfaceActions = useMemo<SurfaceActions>(
    () => ({
      sendMessage: (text) => void sendMessage(text),
      submitForm: (formId, values) => void sendMessage(`Form ${formId} submitted`, { form: { formId, values }, display: `${formId}: ${Object.entries(values).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(', ')}` }),
      pickSlot: (slot: Slot) =>
        void sendMessage(`Book the slot on ${slot.localDate} at ${slot.localTime}`, {
          form: { formId: 'slot-pick', values: { providerId: slot.providerId, start: slot.start, appointmentTypeCode: slot.appointmentTypeCode } },
        }),
      confirm,
      setPatientContext: (patient: PatientBanner | null, activeEncounterId: string | null) =>
        setContext((prev) =>
          prev.activePatient?.patientId === (patient?.patientId ?? undefined) && prev.activeEncounterId === activeEncounterId && (prev.activePatient === null) === (patient === null)
            ? prev
            : { activePatient: patient, activeEncounterId },
        ),
    }),
    [sendMessage, confirm],
  );

  // ---------------------------------------------------------------- voice input
  const getConfig = useCallback((): VoiceConfig => {
    const e = effectiveRef.current;
    const l = localRef.current;
    const language = l.inputLanguage === 'auto' ? undefined : l.inputLanguage;
    return {
      ...(language ? { language } : {}),
      autoLanguageIdentification: !language,
      silenceThresholdMs: e.silenceThresholdMs,
      maxCommandSeconds: e.maxCommandSeconds,
      mode: e.mode,
    };
  }, []);
  const onCommand = useCallback((cmd: VoiceCommand) => void sendMessage(cmd.text, { inputMode: 'voice', transcript: cmd.meta }), [sendMessage]);
  const voice = useVoiceCapture({ api, getConfig, onCommand }, onSignOut);
  const voiceState = voice.state;
  const controller = voice.controller;

  // Barge-in: the moment the microphone opens (button, shortcut or hold), any assistant speech stops so the
  // phone never hears the assistant and feeds it back as a command. Also stops the "loading" phase before playback.
  useEffect(
    () =>
      controller.subscribe(() => {
        const s = controller.getSnapshot().status;
        if (s === 'requestingMic' || s === 'connecting' || s === 'listening') speaker.stop();
      }),
    [controller, speaker],
  );
  const micToggle = useCallback(() => {
    primeAudioContext(); // inside the gesture: iOS only lets audio start from a tap/keypress
    speaker.stop();
    controller.toggle();
  }, [controller, speaker]);
  const micPressStart = useCallback(() => {
    primeAudioContext();
    speaker.stop();
    controller.pressStart();
  }, [controller, speaker]);

  // Sign-out / expiry: stop playback, clear the thread. (Mic shutdown is bound inside useVoiceCapture.)
  useEffect(
    () =>
      onSignOut(() => {
        speaker.stop();
        setTurns([]);
      }),
    [speaker],
  );

  // Keyboard shortcuts: toggle / push-to-talk / Esc. Only when the window has focus (keydown implies it)
  // and the target is not an editable element other than the composer.
  useEffect(() => {
    const isComposer = (el: Element): boolean => el.hasAttribute(COMPOSER_ATTR);
    let holding: string | undefined;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (controller.isActive || controller.status === 'error') {
          controller.cancel('CANCELLED');
          controller.clearError();
        }
        speaker.stop();
        return;
      }
      if (!isShortcutAllowedTarget(e.target, isComposer)) return;
      const eff = effectiveRef.current;
      if (matchesShortcut(eff.shortcut, e)) {
        e.preventDefault();
        if (eff.mode === 'PRESS_AND_HOLD') {
          if (e.repeat) return;
          holding = eff.shortcut;
          micPressStart();
        } else if (!e.repeat) micToggle();
        return;
      }
      if (matchesShortcut(eff.pushToTalkShortcut, e)) {
        e.preventDefault();
        if (e.repeat) return;
        holding = eff.pushToTalkShortcut;
        micPressStart();
      }
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (holding && releasesShortcut(holding, e)) {
        holding = undefined;
        controller.pressEnd();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [controller, speaker, micToggle, micPressStart]);

  // ---------------------------------------------------------------- preferences
  const savePreferences = useCallback(async (patch: Partial<UserPreferences>) => {
    setPreferences((p) => ({ ...p, ...patch }));
    const saved = await api.updatePreferences(patch);
    setPreferences(saved);
    if (patch.voiceOutputEnabled === false) speaker.stop();
  }, [speaker]);

  const toggleVoiceOutput = useCallback(() => {
    const next = !prefsRef.current.voiceOutputEnabled;
    if (!next) speaker.stop();
    void savePreferences({ voiceOutputEnabled: next }).catch(() => {});
  }, [savePreferences, speaker]);

  const openPatientHelper = useCallback(() => {
    setTurns((prev) => [
      ...prev,
      {
        id: nextTurnId(),
        role: 'assistant',
        text: '',
        local: true,
        at: Date.now(),
        surfaces: [
          {
            type: 'clarification',
            question: t('whichPatient'),
            reason: 'MISSING_PARAMETER',
            options: [
              { id: 'who', label: t('whoIsActive'), reply: 'Who is my active patient?' },
              ...(context.activePatient ? [{ id: 'clear', label: t('clearContext'), reply: 'Clear the current patient context' }] : []),
            ],
          },
        ],
      },
    ]);
  }, [t, context.activePatient]);

  return (
    <SurfaceActionsContext.Provider value={surfaceActions}>
      <div className="app">
        <TopBar
          context={context}
          voiceState={voiceState}
          voiceOutputEnabled={preferences.voiceOutputEnabled}
          speaking={speakerState.speaking || speakerState.loading}
          onPatientClick={openPatientHelper}
          onToggleVoiceOutput={toggleVoiceOutput}
          onStopSpeaking={() => speaker.stop()}
          onOpenSettings={() => setSettingsOpen(true)}
          onCancelVoice={() => {
            controller.cancel('CANCELLED');
            controller.clearError();
          }}
          onSignOut={signOut}
          {...(me ? { tenantName: me.tenantName, displayName: me.displayName } : {})}
        />
        {meError ? (
          <div className="banner error" role="alert">
            {meError}{' '}
            <button type="button" className="btn btn-small" onClick={() => window.location.reload()}>
              {t('retry')}
            </button>
          </div>
        ) : null}
        <Thread turns={turns} busy={busy} />
        <Composer
          onSend={(text) => void sendMessage(text)}
          voiceState={voiceState}
          mode={effective.mode}
          toggleShortcut={effective.shortcut}
          pushToTalkShortcut={effective.pushToTalkShortcut}
          onMicToggle={micToggle}
          onMicPressStart={micPressStart}
          onMicPressEnd={() => controller.pressEnd()}
          busy={busy}
        />
        {settingsOpen && me ? (
          <SettingsPanel me={me} preferences={preferences} local={local} effective={effective} onSavePreferences={savePreferences} onClose={() => setSettingsOpen(false)} />
        ) : null}
      </div>
    </SurfaceActionsContext.Provider>
  );
}
